import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Sparkles, Info, Save, RotateCcw, Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/components/proposals/proposalUtils";
import {
  wcConfig, wcQuote, WC_DEFAULTS, WC_OPENING_KEYS, WC_COUNT_KEYS,
  wcCountsFromConfig, wcApplyCounts, wcCountsAreEmpty,
} from "@/components/proposals/windowCare";
import { getWindowCareDefaults, getWindowCareTemplate, saveWindowCareTemplate } from "@/lib/api";

// Window Care estimator. The estimator types pane counts per visit; price and
// sub cost come from the library and stay editable here. Counts can be saved
// against the site ("this building's pane counts") and auto-load on the next
// quote for that site. Everything it produces is written back as ordinary
// subcontract line items by the parent.
export const WindowCarePanel = ({ scope, scopeIdx, onChange, siteId, siteName }) => {
  const cfg = wcConfig(scope);
  const q = wcQuote(cfg);
  const seeded = useRef(false);
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);

  // Seed: library rates + this site's saved pane counts (auto-fill).
  useEffect(() => {
    if (seeded.current || scope.window_care) return;
    seeded.current = true;
    (async () => {
      let base = { ...WC_DEFAULTS, enabled: true, visits: 1 };
      try {
        const { data } = await getWindowCareDefaults();
        base = { ...base, ...data, enabled: true, visits: 1 };
      } catch { /* library unreachable — fall back to built-in rates */ }
      if (siteId) {
        try {
          const { data } = await getWindowCareTemplate(siteId);
          if (data?.template?.counts) {
            setTpl(data.template);
            base = wcApplyCounts(base, data.template.counts);
            toast.success(`Loaded saved pane counts for ${data.site_name || "this site"}`);
          }
        } catch { /* no template yet */ }
      }
      onChange(base);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.id]);

  // Site picked (or changed) after the scope was added — refresh the saved
  // counts and auto-apply them while the estimator is still empty.
  useEffect(() => {
    if (!siteId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await getWindowCareTemplate(siteId);
        if (!alive) return;
        setTpl(data?.template || null);
        if (data?.template?.counts && scope.window_care
            && wcCountsAreEmpty(wcCountsFromConfig(wcConfig(scope)))) {
          onChange(wcApplyCounts(wcConfig(scope), data.template.counts));
          toast.success(`Loaded saved pane counts for ${data.site_name || "this site"}`);
        }
      } catch { if (alive) setTpl(null); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId]);

  const set = (patch) => onChange({ ...cfg, ...patch });
  const setRate = (key, field, value) =>
    set({ rates: cfg.rates.map(r => (r.key === key ? { ...r, [field]: value } : r)) });

  const loadCounts = () => {
    if (!tpl?.counts) return;
    onChange(wcApplyCounts(cfg, tpl.counts));
    toast.success("Saved pane counts loaded");
  };

  const saveCounts = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      const { data } = await saveWindowCareTemplate(siteId, {
        counts: wcCountsFromConfig(cfg),
        last_quoted_total: q.totalPrice,
        source: "estimator",
      });
      setTpl(data.template);
      toast.success(`Pane counts saved to ${data.site_name || "this site"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the pane counts");
    } finally {
      setSaving(false);
    }
  };

  const counts = wcCountsFromConfig(cfg);
  const dirty = !tpl?.counts
    ? !wcCountsAreEmpty(counts)
    : WC_COUNT_KEYS.some(k => Number(tpl.counts[k] || 0) !== Number(counts[k] || 0))
      || !!tpl.counts.both_sides !== !!counts.both_sides;

  const Money = ({ label, value, strong }) => (
    <div className="flex items-baseline justify-between gap-3">
      <span className={`text-xs ${strong ? "font-semibold text-slate-700" : "text-slate-500"}`}>{label}</span>
      <span className={`tabular-nums ${strong ? "text-sm font-bold text-slate-900" : "text-xs text-slate-700"}`}>{formatCurrency(value)}</span>
    </div>
  );

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-3 space-y-3" data-testid={`window-care-panel-${scopeIdx}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="w-4 h-4 text-sky-600" />
        <span className="text-sm font-semibold text-sky-900">Window Care estimator</span>
        <span className="text-[11px] text-sky-700/80">Subcontracted — enter pane counts per visit</span>
        <div className="ml-auto flex items-center gap-1.5">
          {tpl?.counts && (
            <Button type="button" variant="ghost" size="sm" className="h-7 text-[11px] gap-1 text-slate-600"
              onClick={loadCounts} data-testid={`wc-load-counts-${scopeIdx}`}>
              <RotateCcw className="w-3 h-3" /> Reload saved counts
            </Button>
          )}
          <Button type="button" size="sm" variant="outline"
            className="h-7 text-[11px] gap-1 border-sky-300 text-sky-700 hover:bg-sky-100"
            disabled={!siteId || saving || !dirty}
            onClick={saveCounts} data-testid={`wc-save-counts-${scopeIdx}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {tpl?.counts ? "Update site counts" : "Save counts to site"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <Building2 className="w-3 h-3" />
          {siteId ? (siteName || "Selected site") : "Pick a site on the proposal to save these counts"}
        </span>
        {tpl?.counts && (
          <span data-testid={`wc-template-meta-${scopeIdx}`}>
            Saved {String(tpl.updated_at || "").slice(0, 10)}
            {tpl.updated_by ? ` by ${tpl.updated_by}` : ""}
            {tpl.source === "field" ? " (field correction)" : ""}
            {tpl.last_quoted_total != null ? ` · last quoted ${formatCurrency(tpl.last_quoted_total)}` : ""}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-3">
        {/* Rate sheet */}
        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-[1.6fr_0.6fr_0.8fr_0.8fr_0.9fr] gap-2 px-2 py-1.5 bg-slate-50 border-b text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
            <span>Item</span><span className="text-center">Qty</span><span>Sub cost</span><span>Price</span><span className="text-right">Extended</span>
          </div>
          {cfg.rates.map((r) => {
            const derived = cfg.both_sides && r.key === "interior";
            const ext = (parseFloat(r.qty) || 0) * (parseFloat(r.price) || 0);
            return (
              <div key={r.key} className="grid grid-cols-[1.6fr_0.6fr_0.8fr_0.8fr_0.9fr] gap-2 px-2 py-1.5 items-center border-b last:border-0">
                <span className="text-xs text-slate-700 leading-tight">
                  {r.name}
                  {derived && <span className="ml-1 text-[9px] text-sky-600 font-semibold">AUTO</span>}
                </span>
                {derived ? (
                  <div className="h-7 flex items-center justify-center bg-sky-50 border border-sky-100 rounded text-xs tabular-nums text-sky-700"
                    data-testid={`wc-qty-${scopeIdx}-${r.key}`}>{r.qty}</div>
                ) : (
                  <Input
                    type="number" min="0" step="1"
                    value={r.qty === 0 || r.qty === "0" ? "" : r.qty}
                    placeholder="0"
                    onChange={e => setRate(r.key, "qty", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                    className="h-7 text-xs text-center"
                    data-testid={`wc-qty-${scopeIdx}-${r.key}`}
                  />
                )}
                <Input
                  type="number" step="0.01" value={r.sub_cost ?? ""}
                  onChange={e => setRate(r.key, "sub_cost", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                  className="h-7 text-xs"
                  data-testid={`wc-subcost-${scopeIdx}-${r.key}`}
                />
                <Input
                  type="number" step="0.01" value={r.price ?? ""}
                  onChange={e => setRate(r.key, "price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
                  className="h-7 text-xs border-sky-300 bg-sky-50/40"
                  data-testid={`wc-price-${scopeIdx}-${r.key}`}
                />
                <span className="text-xs tabular-nums text-right text-slate-600">{formatCurrency(ext)}</span>
              </div>
            );
          })}
          <label className="flex items-center gap-2 px-2 py-2 bg-sky-50/60 border-t cursor-pointer">
            <input
              type="checkbox" checked={!!cfg.both_sides}
              onChange={e => set({ both_sides: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-sky-300 text-sky-600 focus:ring-sky-500"
              data-testid={`wc-both-sides-${scopeIdx}`}
            />
            <span className="text-xs text-slate-700">
              Interior <span className="text-slate-400">+</span> exterior
              <span className="text-[11px] text-slate-500"> — prices the interior add on every opening ({WC_OPENING_KEYS.length === 2 ? "standard + large" : "all openings"})</span>
            </span>
          </label>
        </div>

        {/* Controls + build-up */}
        <div className="space-y-2">
          <div className="bg-white rounded-md border border-slate-200 p-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-400">Visits</Label>
                <Input type="number" min="1" step="1" value={cfg.visits}
                  onChange={e => set({ visits: Math.max(1, parseInt(e.target.value) || 1) })}
                  className="h-7 text-xs" data-testid={`wc-visits-${scopeIdx}`} />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Deep clean adder %</Label>
                <Input type="number" min="0" max="100" step="1" value={cfg.adder_pct}
                  disabled={!cfg.adder_enabled}
                  onChange={e => set({ adder_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })}
                  className="h-7 text-xs" data-testid={`wc-adder-pct-${scopeIdx}`} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-600">Initial deep clean / logistics adder</span>
              <Switch checked={!!cfg.adder_enabled} onCheckedChange={v => set({ adder_enabled: v })}
                data-testid={`wc-adder-toggle-${scopeIdx}`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-slate-400">Min / visit (customer)</Label>
                <Input type="number" step="0.01" value={cfg.min_price}
                  onChange={e => set({ min_price: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })}
                  className="h-7 text-xs" data-testid={`wc-min-price-${scopeIdx}`} />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400">Min / visit (sub)</Label>
                <Input type="number" step="0.01" value={cfg.min_sub}
                  onChange={e => set({ min_sub: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })}
                  className="h-7 text-xs" data-testid={`wc-min-sub-${scopeIdx}`} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-md border border-slate-200 p-2 space-y-1" data-testid={`wc-breakdown-${scopeIdx}`}>
            <Money label="Panes (per visit)" value={q.basePrice} />
            {q.adderPrice > 0 && <Money label={`Deep clean adder ${q.adderPct}%`} value={q.adderPrice} />}
            {q.minimumApplied && (
              <Money label={`Job minimum top-up (to ${formatCurrency(q.minPrice)})`} value={q.topupPrice} />
            )}
            <Money label={`Per visit${q.visits > 1 ? ` × ${q.visits}` : ""}`} value={q.visitPrice + q.topupPrice} />
            <div className="border-t pt-1 mt-1 space-y-1">
              <Money label="Customer Total" value={q.totalPrice} strong />
              <Money label="Sub cost (pass-through)" value={q.totalSub} />
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-xs text-slate-500">Profit / GM%</span>
                <span className={`text-xs tabular-nums font-semibold ${q.marginPct >= 30 ? "text-green-600" : "text-amber-600"}`} data-testid={`wc-margin-${scopeIdx}`}>
                  {formatCurrency(q.profit)} · {q.marginPct.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="flex items-start gap-1.5 text-[11px] text-slate-500">
        <Info className="w-3 h-3 mt-0.5 shrink-0" />
        The adder raises the customer price and the sub cost. Lines below are generated from this panel —
        edit a price or sub cost here (or in the line itself) and the totals follow. Saved counts are per site and
        show up on the site's Window Care equipment record.
      </p>
    </div>
  );
};

export default WindowCarePanel;
